/**
 * TenderPredictions — Tab 7: Risk-weighted pipeline + revenue forecast +
 * prediction detail with factor breakdown.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { RefreshCcw, Sparkles, TrendingUp, ChevronRight, Brain } from 'lucide-react';
import api from '@/lib/api';
import RevenueForecastChart from './RevenueForecastChart';

const fmtINR = (v) => {
  if (v === null || v === undefined) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(1)} Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(1)} L`;
  return `₹${v.toFixed(0)}`;
};

const FactorBar = ({ factor }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="text-slate-300 w-44 truncate">{factor.factorName}</span>
    <div className="flex-1 h-2 bg-slate-800 rounded">
      <div className="h-full rounded bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]"
            style={{ width: `${Math.min(factor.score || 0, 100)}%` }} />
    </div>
    <span className="text-slate-400 w-12 text-right">{(factor.score || 0).toFixed(0)}/100</span>
    <span className="text-slate-500 w-14 text-right">({(factor.weight || 0).toFixed(0)}%)</span>
    <span className="text-emerald-400 w-14 text-right font-mono">
      +{(factor.weightedScore || 0).toFixed(1)}
    </span>
  </div>
);

const TenderPredictions = () => {
  const [pipeline, setPipeline] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [activePred, setActivePred] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accuracy, setAccuracy] = useState(null);

  const loadAll = async () => {
    try {
      const [p, f, a] = await Promise.all([
        api.get('/tenders/predictions/pipeline-summary'),
        api.get('/tenders/predictions/revenue-forecast'),
        api.get('/tenders/predictions/accuracy').catch(() => ({ data: null })),
      ]);
      setPipeline(p.data);
      setForecast(f.data);
      setAccuracy(a.data);
    } catch (err) {
      toast.error('Failed to load predictions');
    }
  };
  useEffect(() => { loadAll(); }, []);

  const handleGenerateOne = async (tenderId) => {
    setBusy(true);
    try {
      const start = await api.post(`/tenders/predictions/${tenderId}/generate`);
      const jobId = start.data?.jobId;
      // Poll
      const deadline = Date.now() + 90 * 1000;
      let job = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 1500));
        const poll = await api.get(`/tenders/predictions/status/${jobId}`);
        job = poll.data;
        if (job?.status === 'done' || job?.status === 'failed') break;
      }
      if (job?.status === 'done') {
        toast.success('Prediction generated');
        const latest = await api.get(`/tenders/predictions/${tenderId}/latest`);
        setActivePred(latest.data);
        await loadAll();
      } else {
        toast.error(job?.error || 'Prediction failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate');
    } finally {
      setBusy(false);
    }
  };

  const handleBulkRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/tenders/predictions/bulk-refresh');
      toast.success(`Scheduled ${r.data.scheduled} predictions — refresh in ~1 min`);
      // Auto-refresh after delay
      setTimeout(loadAll, 30000);
    } catch (err) {
      toast.error('Bulk refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleViewDetail = async (tenderId) => {
    try {
      const r = await api.get(`/tenders/predictions/${tenderId}/latest`);
      setActivePred(r.data);
    } catch {
      toast.info('No prediction yet — generating…');
      handleGenerateOne(tenderId);
    }
  };

  return (
    <div className="space-y-4" data-testid="tender-predictions-tab">
      {/* Model Accuracy Banner (live backtest) */}
      {accuracy && (
        <Card className="bg-gradient-to-r from-[#0F1B2E] to-[#1F2937] border-[#6B5CE7]/40 border" data-testid="model-accuracy-banner">
          <CardContent className="p-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Model Accuracy (last {Math.round(accuracy.windowDays / 30)}mo)</p>
                <p className="text-lg font-bold text-white">
                  {accuracy.hitRatePct != null
                    ? <>{accuracy.hitRatePct}% <span className="text-xs text-slate-500 font-normal">hit rate</span></>
                    : <span className="text-slate-500 text-sm">no scored predictions yet</span>}
                </p>
              </div>
              {accuracy.totalScored > 0 && (
                <>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Scored</p>
                    <p className="text-sm text-slate-100">{accuracy.correct} correct / {accuracy.totalScored}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Brier Score</p>
                    <p className="text-sm text-slate-100">
                      {accuracy.brierScore ?? '—'}
                      <span className="text-slate-500 text-xs ml-1">(lower is better)</span>
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className="text-[10px] text-slate-400 max-w-xs">
              {accuracy.totalScored === 0
                ? 'Once a tender is closed WON/LOST, every open prediction is auto-scored against reality and shown here.'
                : 'Predictions ≥50% that WON + <50% that LOST count as correct. Brier scores predictions against the actual 0/1 outcome.'}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-[#1F2937] border-[#3A3F4A]">
          <CardContent className="p-3">
            <p className="text-[10px] text-slate-500 uppercase">Active Tenders</p>
            <p className="text-xl font-bold text-white mt-1">{pipeline?.count ?? '—'}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#1F2937] border-[#3A3F4A]">
          <CardContent className="p-3">
            <p className="text-[10px] text-slate-500 uppercase">Total Pipeline</p>
            <p className="text-xl font-bold text-white mt-1">{fmtINR(pipeline?.totalPipelineValue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#1F2937] border-emerald-500/40 border">
          <CardContent className="p-3">
            <p className="text-[10px] text-slate-500 uppercase">Risk-Weighted</p>
            <p className="text-xl font-bold text-emerald-400 mt-1">{fmtINR(pipeline?.totalRiskWeightedValue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#1F2937] border-[#3A3F4A]">
          <CardContent className="p-3 flex items-center justify-between">
            <div>
              <p className="text-[10px] text-slate-500 uppercase">Refresh</p>
              <p className="text-xs text-slate-300 mt-1">Re-run all predictions</p>
            </div>
            <Button size="sm" disabled={refreshing} onClick={handleBulkRefresh}
                    className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]" data-testid="bulk-refresh-btn">
              <RefreshCcw className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh All
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline grid */}
      <Card className="bg-[#1F2937] border-[#3A3F4A]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Brain className="w-4 h-4 text-[#6B5CE7]" />Risk-Weighted Pipeline
            </h3>
            <span className="text-xs text-slate-500">powered by Cognara</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-400 border-b border-[#3A3F4A]">
                <tr>
                  <th className="text-left p-2">Tender</th>
                  <th className="text-left p-2">Client</th>
                  <th className="text-left p-2">Sector</th>
                  <th className="text-right p-2">Value</th>
                  <th className="text-right p-2">Win %</th>
                  <th className="text-right p-2">Weighted</th>
                  <th className="text-left p-2">Status</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {(pipeline?.tenders || []).map((t) => (
                  <tr key={t.tenderId} className="border-b border-[#3A3F4A]/50 hover:bg-slate-800/40 cursor-pointer"
                      onClick={() => handleViewDetail(t.tenderId)}
                      data-testid={`pipeline-row-${t.tenderId}`}>
                    <td className="p-2 font-mono text-slate-200">{t.tenderNumber || '—'}</td>
                    <td className="p-2 text-slate-300 truncate max-w-[180px]" title={t.tenderName}>
                      {t.tenderName || t.client}
                    </td>
                    <td className="p-2 text-slate-400">{t.sector || '—'}</td>
                    <td className="p-2 text-right text-slate-300">{fmtINR(t.estimatedValue)}</td>
                    <td className="p-2 text-right">
                      <Badge variant="outline" className={
                        t.winProbabilityPct >= 70 ? 'border-emerald-500 text-emerald-400'
                        : t.winProbabilityPct >= 40 ? 'border-amber-500 text-amber-400'
                        : 'border-red-500 text-red-400'
                      }>
                        {t.winProbabilityPct?.toFixed(0)}%
                      </Badge>
                    </td>
                    <td className="p-2 text-right text-emerald-400 font-medium">
                      {fmtINR(t.riskWeightedValue)}
                    </td>
                    <td className="p-2"><span className="text-slate-400">{t.status}</span></td>
                    <td className="p-2 text-right">
                      <Button size="sm" variant="ghost" disabled={busy}
                              onClick={(e) => { e.stopPropagation(); handleGenerateOne(t.tenderId); }}
                              className="h-6 px-2 text-[#6B5CE7] hover:bg-[#6B5CE7]/20 text-[10px]"
                              data-testid={`gen-pred-${t.tenderId}`}>
                        <Sparkles className="w-3 h-3 mr-1" />Predict
                      </Button>
                    </td>
                  </tr>
                ))}
                {!pipeline?.tenders?.length && (
                  <tr><td colSpan={8} className="p-6 text-center text-slate-500">
                    No active tenders in the pipeline.
                  </td></tr>
                )}
                {pipeline?.tenders?.length > 0 && (
                  <tr className="font-semibold border-t-2 border-[#3A3F4A] bg-slate-900/40">
                    <td className="p-2 text-slate-400" colSpan={3}>TOTAL</td>
                    <td className="p-2 text-right text-white">{fmtINR(pipeline.totalPipelineValue)}</td>
                    <td></td>
                    <td className="p-2 text-right text-emerald-400">{fmtINR(pipeline.totalRiskWeightedValue)}</td>
                    <td colSpan={2}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Forecast chart */}
      <Card className="bg-[#1F2937] border-[#3A3F4A]">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />Forward Revenue Forecast
          </h3>
          {forecast?.expectedAnnualRevenue?.length > 0
            ? <RevenueForecastChart data={forecast.expectedAnnualRevenue} />
            : <p className="text-xs text-slate-500 p-8 text-center">
                No active tenders with predicted revenue yet. Click &ldquo;Predict&rdquo; on any pipeline row to begin.
              </p>}
        </CardContent>
      </Card>

      {/* Prediction Detail Panel */}
      {activePred && (
        <Card className="bg-[#1F2937] border-[#6B5CE7]/40 border-2">
          <CardContent className="p-4 space-y-3" data-testid="prediction-detail">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">
                {activePred.tenderNumber}: {(activePred.prediction?.factors || []).length
                  ? `Prediction Detail`
                  : 'Loading…'}
              </h3>
              <Badge variant="outline" className="border-[#6B5CE7] text-[#6B5CE7]">
                Win Probability: {activePred.prediction?.winProbabilityPct}%
                · Confidence {activePred.prediction?.confidenceLevel}
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                {(activePred.prediction?.factors || []).map((f) => (
                  <FactorBar key={f.factorName} factor={f} />
                ))}
              </div>
              <div className="space-y-3 text-xs">
                {/* Historical Anchor — evidence-based Bayesian shrinkage */}
                {(activePred.prediction?.anchorNarrative || activePred.prediction?.baseRateEvidence) && (
                  <div className="rounded-md border border-[#6B5CE7]/40 bg-[#6B5CE7]/5 p-2.5" data-testid="anchor-evidence-panel">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#6B5CE7]" />
                      <p className="text-[#B5A9FF] font-semibold uppercase tracking-wider text-[10px]">Historical Anchor</p>
                    </div>
                    <p className="text-slate-200 leading-relaxed" data-testid="anchor-narrative">
                      {activePred.prediction.anchorNarrative}
                    </p>
                    {activePred.prediction?.baseRateEvidence && (
                      <div className="mt-2 grid grid-cols-3 gap-1.5 text-[10px] font-mono">
                        <div className="rounded bg-slate-900/60 px-1.5 py-1">
                          <div className="text-slate-500">Cognara</div>
                          <div className="text-slate-100 text-sm">{activePred.prediction.claudeWinProbabilityPct?.toFixed?.(1) ?? '—'}%</div>
                        </div>
                        <div className="rounded bg-slate-900/60 px-1.5 py-1">
                          <div className="text-slate-500">Base rate</div>
                          <div className="text-slate-100 text-sm">{activePred.prediction.baseRateWinProbabilityPct?.toFixed?.(1) ?? '—'}%</div>
                        </div>
                        <div className="rounded bg-[#6B5CE7]/15 px-1.5 py-1 border border-[#6B5CE7]/40">
                          <div className="text-[#B5A9FF]">Final</div>
                          <div className="text-white text-sm font-bold">{activePred.prediction.winProbabilityPct?.toFixed?.(1) ?? '—'}%</div>
                        </div>
                      </div>
                    )}
                    {activePred.prediction?.baseRateEvidence && (
                      <div className="mt-2 text-[10px] text-slate-400 flex flex-wrap gap-x-3 gap-y-1">
                        <span>slice: <b className="text-slate-200">{activePred.prediction.baseRateEvidence.chosenSlice}</b></span>
                        <span>evidence weight: <b className="text-slate-200">{Number(activePred.prediction.baseRateEvidence.chosenEvidence || 0).toFixed(2)}</b></span>
                        <span>anchor weight: <b className="text-slate-200">{Math.round((activePred.prediction.anchorWeight || 0) * 100)}%</b></span>
                        <span>recency half-life: <b className="text-slate-200">{activePred.prediction.baseRateEvidence.recencyHalfLifeMonths}mo</b></span>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <p className="text-slate-400 font-medium mb-1">Cognara Narrative</p>
                  <p className="text-slate-300">{activePred.prediction?.narrative || '—'}</p>
                </div>
                <div>
                  <p className="text-emerald-400 font-medium mb-1">Key Strengths</p>
                  <ul className="text-slate-300 list-disc list-inside space-y-0.5">
                    {(activePred.prediction?.keyStrengths || []).map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
                <div>
                  <p className="text-amber-400 font-medium mb-1">Key Risks</p>
                  <ul className="text-slate-300 list-disc list-inside space-y-0.5">
                    {(activePred.prediction?.keyRisks || []).map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
                {(activePred.prediction?.comparables || []).length > 0 && (
                  <div>
                    <p className="text-slate-400 font-medium mb-1">Historical Comparables</p>
                    <ul className="text-slate-400 space-y-0.5 text-[11px]">
                      {activePred.prediction.comparables.slice(0, 3).map((c, i) => (
                        <li key={i}>
                          <ChevronRight className="w-3 h-3 inline" /> {c.tenderNumber}: {c.tenderName} —{' '}
                          <span className={c.outcome === 'WON' ? 'text-emerald-400' : 'text-red-400'}>
                            {c.outcome}
                          </span>{' '}
                          ({c.similarityScore}% similar)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TenderPredictions;
